import { Box, Button, Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { headerTypographyProps } from '../../../styles/sxStyling';
import { EmployeeRequirement } from '../../../types/EmployeeRequirement';
import { Preference, PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { uuidFinder } from '../../../utility/inputUtils';
import PreferencePicker from '../../preferences/picker/PreferencePicker';

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (req: EmployeeRequirement) => void;
  requirement: EmployeeRequirement | null;
  selectedEmployeeRoles: string[];
}

type ModalState = Omit<EmployeeRequirement, 'uuid'>;

const BLANK_STATE: ModalState = {
  employee_role_uuid: '',
  employee_role_value: '',
  employee_role_color: '',
  quantity: 1,
};

export default function AddEmployeeRequirementModal({ isOpen, onCancel, onSubmit, requirement, selectedEmployeeRoles }: Props) {
  const [state, setState] = useState<ModalState>(BLANK_STATE);
  const [touched, setTouched] = useState(false);

  const { data: preferencesData, isFetching } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.EMPLOYEE_ROLE] } },
  });

  const roles = preferencesData || [];

  useEffect(() => {
    // Editing → preserve parent_requirement_uuid if present
    // Adding → clean slate (no parent)
    if (requirement) {
      const { uuid, ...rest } = requirement;
      setState(rest);
    } else {
      setState(BLANK_STATE);
    }
    setTouched(false);
  }, [requirement, isOpen]);

  /**
   * Prevent duplicate employee roles.
   * While editing, allow the current role.
   */
  const availableRoles = useMemo(() => {
    const excluded = requirement ? selectedEmployeeRoles.filter((uuid) => uuid !== requirement.employee_role_uuid) : selectedEmployeeRoles;

    return roles.filter((role) => !excluded.includes(role.uuid));
  }, [roles, selectedEmployeeRoles, requirement]);

  const selectedRole = (uuidFinder(availableRoles, state.employee_role_uuid) as Preference | null) ?? null;

  const roleMissing = _.isEmpty(state.employee_role_uuid);
  const quantityInvalid = _.isNil(state.quantity) || Number.isNaN(state.quantity) || state.quantity <= 0;

  const formInvalid = roleMissing || quantityInvalid;

  const quantityError = touched && quantityInvalid ? 'Quantity must be greater than 0' : '';

  const handleSubmit = () => {
    setTouched(true);
    if (formInvalid) return;

    const payload: EmployeeRequirement = {
      uuid: requirement?.uuid || crypto.randomUUID(),
      ...state,
      employee_role_color: selectedRole?.color ?? state.employee_role_color ?? '',
    };

    // Never allow parent on manual adds
    if (!requirement) {
      delete (payload as any).parent_requirement_uuid;
    }

    onSubmit(payload);
    onCancel();
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Typography {...headerTypographyProps} sx={{ m: 2 }}>
        {requirement ? 'Edit Employee Requirement' : 'Add Employee Requirement'}
      </Typography>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PreferencePicker
            label="Employee Role"
            options={availableRoles}
            loading={isFetching}
            value={selectedRole}
            disabled={!!requirement}
            onValueChange={(val) => {
              const role = val as Preference | null;
              setState((prev) => ({
                ...prev,
                employee_role_uuid: role?.uuid ?? '',
                employee_role_value: role?.value ?? '',
              }));
            }}
          />

          <TextField
            label="Quantity"
            type="number"
            inputProps={{ min: 1, step: 1 }}
            value={state.quantity}
            error={!!quantityError}
            helperText={quantityError}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                quantity: _.toNumber(e.target.value),
              }))
            }
            onBlur={() => setTouched(true)}
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" disabled={formInvalid} onClick={handleSubmit}>
          {requirement ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
